import fastJsonPatch from 'fast-json-patch'
import merge from 'lodash.merge'
import { useState, useContext, createContext, useEffect } from 'react'
import type { Dispatch, SetStateAction } from 'react'

import type {
  AlertGate,
  BetaFeatures,
  Coordinates,
  Depth,
  TableColumnPrefs,
  Temperature,
  Timezone,
  TransmissionThresholds,
} from '@beaded/models'

import { isAlertGate, mergeGatesForSave } from 'lib/alertGates'
import { DEFAULT_THRESHOLDS, resolveThresholds } from 'lib/alertThresholds'

import { useAuthContext } from './useUserContext'
import { useViewAs } from './useViewAs'

export interface IUserPrefsContext {
  temp: Temperature
  setTemp: Dispatch<SetStateAction<Temperature>>
  timezone: Timezone
  setTimezone: Dispatch<SetStateAction<Timezone>>
  coordinates: Coordinates
  setCoordinates: Dispatch<SetStateAction<Coordinates>>
  depth: Depth
  setDepth: Dispatch<SetStateAction<Depth>>
  compactCableBuilder: boolean
  setCompactCableBuilder: Dispatch<SetStateAction<boolean>>
  beta: BetaFeatures
  setBeta: Dispatch<SetStateAction<BetaFeatures>>
  tableColumns: TableColumnPrefs
  alertGates: AlertGate[]
  setAlertGates: Dispatch<SetStateAction<AlertGate[]>>
  transmissionThresholds: TransmissionThresholds
  setTransmissionThresholds: Dispatch<SetStateAction<TransmissionThresholds>>
  save: () => void
  saveTableColumnPrefs: (
    tableId: string,
    overrides: Record<string, boolean>,
  ) => void
}

const defaultPrefs: IUserPrefsContext = {
  temp: 'C' as Temperature,
  timezone: { value: 'UTC' } as Timezone,
  coordinates: 'DD' as Coordinates,
  depth: 'ft' as Depth,
  compactCableBuilder: false,
  beta: { all: false } as BetaFeatures,
  tableColumns: {},
  alertGates: [],
  transmissionThresholds: DEFAULT_THRESHOLDS,
  setTemp: () => {},
  setTimezone: () => {},
  setCoordinates: () => {},
  setDepth: () => {},
  setCompactCableBuilder: () => {},
  setBeta: () => {},
  setAlertGates: () => {},
  setTransmissionThresholds: () => {},
  save: () => {},
  saveTableColumnPrefs: () => {},
}

const userPrefsContext = createContext<IUserPrefsContext>(defaultPrefs)

export function ProvideUserPrefs({ children }: { children: React.ReactNode }) {
  const prefs = useProvideUserPrefs()
  return (
    <userPrefsContext.Provider value={prefs}>
      {children}
    </userPrefsContext.Provider>
  )
}

export const useUserPrefsContext = () => {
  return useContext(userPrefsContext)
}

function useProvideUserPrefs(): IUserPrefsContext {
  const { currentUser, patchUserMutation } = useAuthContext()
  const viewAs = useViewAs()

  const [temp, setTemp] = useState<Temperature>('C')
  const [timezone, setTimezone] = useState<Timezone>({ value: 'UTC' })
  const [coordinates, setCoordinates] = useState<Coordinates>('DD')
  const [depth, setDepth] = useState<Depth>('ft')
  const [compactCableBuilder, setCompactCableBuilder] = useState<boolean>(false)
  const [beta, setBeta] = useState<BetaFeatures>({ all: false })
  const [tableColumns, setTableColumns] = useState<TableColumnPrefs>({})
  const [alertGates, setAlertGates] = useState<AlertGate[]>([])
  const [transmissionThresholds, setTransmissionThresholds] =
    useState<TransmissionThresholds>(DEFAULT_THRESHOLDS)

  // Sync prefs from currentUser whenever it changes
  useEffect(() => {
    if (currentUser?.prefs) {
      const prefs = currentUser.prefs
      setTemp(prefs.units?.temperature ?? defaultPrefs.temp)
      setTimezone(prefs.timezone ?? defaultPrefs.timezone)
      setCoordinates(prefs.units?.coordinates ?? defaultPrefs.coordinates)
      setDepth(prefs.units?.depth ?? defaultPrefs.depth)
      setCompactCableBuilder(prefs.compactCableBuilder ?? false)
      setBeta(prefs.beta ?? defaultPrefs.beta)
      setTableColumns(prefs.tableColumns ?? {})
      setAlertGates(
        Array.isArray(prefs.alertGates)
          ? prefs.alertGates.filter(isAlertGate)
          : [],
      )
      setTransmissionThresholds(resolveThresholds(prefs.transmissionThresholds))
    } else {
      setTemp(defaultPrefs.temp)
      setTimezone(defaultPrefs.timezone)
      setCoordinates(defaultPrefs.coordinates)
      setDepth(defaultPrefs.depth)
      setCompactCableBuilder(false)
      setBeta(defaultPrefs.beta)
      setTableColumns({})
      setAlertGates([])
      setTransmissionThresholds(DEFAULT_THRESHOLDS)
    }
  }, [currentUser])

  const saveUserPrefs = () => {
    const mergedPrefs = merge({}, currentUser?.prefs ?? {}, {
      units: { temperature: temp, coordinates, depth },
      timezone,
    })

    // Only write alertGates when the user has gates to save, or the server
    // already has gates (to allow updates/deletions). Skips the write when
    // both sides are empty, avoiding 'add /prefs/alertGates []' patches on
    // every Appearance/units save for gate-less users.
    // Note: JSON.stringify(undefined) returns undefined (not a string), so
    // the old `alertGates === serverGates` comparison was always false when
    // the server had no gates array, making the guard a no-op.
    const serverHasGates = Array.isArray(currentUser?.prefs?.alertGates)

    // Don't freeze default thresholds for users who haven't configured them.
    // Only write transmissionThresholds if the server already has explicit
    // values (the user has saved them before) or if they differ from defaults.
    const serverHasThresholds =
      currentUser?.prefs?.transmissionThresholds != null
    const thresholdsMatchDefaults =
      JSON.stringify(transmissionThresholds) ===
      JSON.stringify(DEFAULT_THRESHOLDS)
    const nextPrefs: Record<string, unknown> = { ...mergedPrefs }
    if (alertGates.length > 0 || serverHasGates) {
      const rawServerGates: unknown[] = Array.isArray(
        currentUser?.prefs?.alertGates,
      )
        ? currentUser.prefs.alertGates
        : []
      nextPrefs.alertGates = mergeGatesForSave(alertGates, rawServerGates)
    }
    if (serverHasThresholds || !thresholdsMatchDefaults) {
      nextPrefs.transmissionThresholds = transmissionThresholds
    }

    const changes = fastJsonPatch.compare(
      { prefs: currentUser?.prefs ?? {} },
      { prefs: nextPrefs },
    )
    patchUserMutation.mutate(changes)
  }

  const saveTableColumnPrefs = (
    tableId: string,
    overrides: Record<string, boolean>,
  ) => {
    const next = { ...tableColumns }
    if (Object.keys(overrides).length === 0) {
      delete next[tableId]
    } else {
      next[tableId] = overrides
    }
    setTableColumns(next)

    // View-as is read-only and must not write the target's prefs.
    if (viewAs) return

    patchUserMutation.mutate([
      { op: 'add', path: '/prefs/tableColumns', value: next },
    ])
  }

  return {
    temp,
    setTemp,
    timezone,
    setTimezone,
    coordinates,
    setCoordinates,
    depth,
    setDepth,
    compactCableBuilder,
    setCompactCableBuilder,
    beta,
    setBeta,
    tableColumns,
    alertGates,
    setAlertGates,
    transmissionThresholds,
    setTransmissionThresholds,
    save: saveUserPrefs,
    saveTableColumnPrefs,
  }
}
